import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Paintbrush,
  Eraser,
  Undo2,
  Trash2,
  Check,
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Sparkles,
  Info,
} from 'lucide-react';
import { ImageSource, Point2D, MaskStroke } from '../types';

interface DamageMaskModalProps {
  isOpen: boolean;
  sourceImage: ImageSource;
  initialMask?: ImageSource | null;
  onApplyMask: (maskImage: ImageSource, regionsSummary: string) => void;
  onClose: () => void;
}

export const DamageMaskModal: React.FC<DamageMaskModalProps> = ({
  isOpen,
  sourceImage,
  initialMask,
  onApplyMask,
  onClose,
}) => {
  const [brushSize, setBrushSize] = useState<number>(18);
  const [isEraser, setIsEraser] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [hasDrawnContent, setHasDrawnContent] = useState<boolean>(!!initialMask);

  // Stroke history for undo
  const [strokes, setStrokes] = useState<MaskStroke[]>([]);
  const currentStrokeRef = useRef<Point2D[]>([]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Setup canvas with photo
  useEffect(() => {
    if (!isOpen) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const w = img.naturalWidth || img.width || 800;
      const h = img.naturalHeight || img.height || 600;

      // Setup photo background canvas
      const bgCanvas = imageCanvasRef.current;
      if (bgCanvas) {
        bgCanvas.width = w;
        bgCanvas.height = h;
        const ctx = bgCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, w, h);
        }
      }

      // Setup transparent mask drawing canvas
      const mCanvas = maskCanvasRef.current;
      if (mCanvas) {
        mCanvas.width = w;
        mCanvas.height = h;
        const mCtx = mCanvas.getContext('2d');
        if (mCtx) {
          mCtx.clearRect(0, 0, w, h);

          // If initial mask provided, load it
          if (initialMask) {
            const maskImg = new Image();
            maskImg.crossOrigin = 'anonymous';
            maskImg.onload = () => {
              mCtx.drawImage(maskImg, 0, 0, w, h);
              setHasDrawnContent(true);
            };
            maskImg.src = initialMask.dataUrl;
          }
        }
      }
    };
    img.src = sourceImage.dataUrl;
  }, [isOpen, sourceImage, initialMask]);

  // Convert client pointer event to normalized 0..1 coordinate
  const getCanvasPoint = useCallback((clientX: number, clientY: number): Point2D | null => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    return { x, y };
  }, []);

  // Redraw mask from stroke history
  const redrawAllStrokes = useCallback((strokeList: MaskStroke[]) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const stroke of strokeList) {
      if (stroke.points.length === 0) continue;

      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = stroke.radius * 2;

      if (stroke.isEraser) {
        ctx.globalCompositeOperation = 'destination-out';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        // Distinct ruby-red mask overlay with 65% opacity
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.72)';
        ctx.fillStyle = 'rgba(239, 68, 68, 0.72)';
      }

      const p0 = stroke.points[0];
      const startX = p0.x * canvas.width;
      const startY = p0.y * canvas.height;

      if (stroke.points.length === 1) {
        ctx.beginPath();
        ctx.arc(startX, startY, stroke.radius, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        for (let i = 1; i < stroke.points.length; i++) {
          const pt = stroke.points[i];
          ctx.lineTo(pt.x * canvas.width, pt.y * canvas.height);
        }
        ctx.stroke();
      }
      ctx.restore();
    }
  }, []);

  // Drawing event handlers
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const pt = getCanvasPoint(e.clientX, e.clientY);
    if (!pt) return;

    setIsDrawing(true);
    currentStrokeRef.current = [pt];

    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize * 2;

    if (isEraser) {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.72)';
      ctx.fillStyle = 'rgba(239, 68, 68, 0.72)';
    }

    const x = pt.x * canvas.width;
    const y = pt.y * canvas.height;
    ctx.beginPath();
    ctx.arc(x, y, brushSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    setHasDrawnContent(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const pt = getCanvasPoint(e.clientX, e.clientY);
    if (!pt) return;

    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prevPt = currentStrokeRef.current[currentStrokeRef.current.length - 1];
    currentStrokeRef.current.push(pt);

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize * 2;

    if (isEraser) {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.72)';
    }

    ctx.beginPath();
    ctx.moveTo(prevPt.x * canvas.width, prevPt.y * canvas.height);
    ctx.lineTo(pt.x * canvas.width, pt.y * canvas.height);
    ctx.stroke();
    ctx.restore();
  };

  const handlePointerUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentStrokeRef.current.length > 0) {
      const newStroke: MaskStroke = {
        points: [...currentStrokeRef.current],
        radius: brushSize,
        isEraser,
      };
      setStrokes((prev) => [...prev, newStroke]);
      currentStrokeRef.current = [];
    }
  };

  const handleUndo = () => {
    if (strokes.length === 0) return;
    const updated = strokes.slice(0, -1);
    setStrokes(updated);
    redrawAllStrokes(updated);
    setHasDrawnContent(updated.length > 0);
  };

  const handleClearMask = () => {
    const canvas = maskCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setStrokes([]);
    setHasDrawnContent(false);
  };

  // Analyze bounding regions of the drawn mask to create targeted prompt instructions
  const generateMaskSummary = (canvas: HTMLCanvasElement): string => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'Localized physical defect zones';

    const w = canvas.width;
    const h = canvas.height;
    const imgData = ctx.getImageData(0, 0, w, h).data;

    let minX = w,
      minY = h,
      maxX = 0,
      maxY = 0;
    let paintedCount = 0;

    // Check pixel alpha channel
    for (let y = 0; y < h; y += 4) {
      for (let x = 0; x < w; x += 4) {
        const alpha = imgData[(y * w + x) * 4 + 3];
        if (alpha > 20) {
          paintedCount++;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (paintedCount === 0) {
      return 'No specific mask painted';
    }

    const normMinX = Math.round((minX / w) * 100);
    const normMaxX = Math.round((maxX / w) * 100);
    const normMinY = Math.round((minY / h) * 100);
    const normMaxY = Math.round((maxY / h) * 100);

    const horizontal =
      normMaxX < 45 ? 'left side' : normMinX > 55 ? 'right side' : 'center & lateral areas';
    const vertical =
      normMaxY < 45 ? 'upper region' : normMinY > 55 ? 'lower region' : 'central quadrant';

    return `Painted defect inpainting zone bounded between X:[${normMinX}%-${normMaxX}%] and Y:[${normMinY}%-${normMaxY}%] (${horizontal}, ${vertical}). Strictly heal cracks and scratches here while keeping untouched surrounding texture natural.`;
  };

  const handleApply = () => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;

    const dataUrl = maskCanvas.toDataURL('image/png');
    const base64Data = dataUrl.split(',')[1];
    const regionsSummary = generateMaskSummary(maskCanvas);

    const maskImageSource: ImageSource = {
      dataUrl,
      base64Data,
      mimeType: 'image/png',
      fileName: `mask_${sourceImage.fileName}`,
      width: maskCanvas.width,
      height: maskCanvas.height,
    };

    onApplyMask(maskImageSource, regionsSummary);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-5 bg-black/90 backdrop-blur-md select-none pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <div className="bg-stone-900 border border-stone-700 rounded-2xl max-w-5xl w-full h-[min(92vh,100dvh)] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
        {/* Top Header & Toolbar */}
        <div className="px-5 py-3 bg-stone-950 border-b border-stone-800 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
              <Paintbrush className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100 flex items-center gap-2">
                Paint Scratch & Tear Damage Mask
                <span className="text-[10px] px-2 py-0.5 rounded bg-red-950/80 text-red-300 border border-red-800 font-mono">
                  Targeted Inpainting
                </span>
              </h3>
              <p className="text-[11px] text-stone-400">
                Brush over severe scratches, paper creases, or chemical stains. The model will focus infilling strictly on your painted zones.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Tool Toggle: Brush vs Eraser */}
            <div className="inline-flex bg-stone-900 rounded-lg p-0.5 border border-stone-800 text-xs">
              <button
                type="button"
                onClick={() => setIsEraser(false)}
                className={`px-3 py-1.5 rounded flex items-center space-x-1.5 transition-colors ${
                  !isEraser
                    ? 'bg-red-600 text-white font-bold'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                <Paintbrush className="w-3.5 h-3.5" />
                <span>Brush</span>
              </button>
              <button
                type="button"
                onClick={() => setIsEraser(true)}
                className={`px-3 py-1.5 rounded flex items-center space-x-1.5 transition-colors ${
                  isEraser
                    ? 'bg-stone-700 text-white font-bold'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                <Eraser className="w-3.5 h-3.5" />
                <span>Eraser</span>
              </button>
            </div>

            {/* Brush Size Slider */}
            <div className="flex items-center space-x-2 px-3 py-1 bg-stone-900 border border-stone-800 rounded-lg text-xs text-stone-300">
              <span className="text-[11px] text-stone-400">Size:</span>
              <input
                type="range"
                min="4"
                max="60"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-20 accent-red-500 cursor-pointer"
              />
              <span className="font-mono text-[11px] text-teal-300 w-6 text-center">{brushSize}px</span>
            </div>

            {/* Undo */}
            <button
              type="button"
              onClick={handleUndo}
              disabled={strokes.length === 0}
              className="p-1.5 text-stone-400 hover:text-teal-300 disabled:opacity-40 rounded-lg hover:bg-stone-800 transition-colors border border-stone-800"
              title="Undo last stroke"
            >
              <Undo2 className="w-4 h-4" />
            </button>

            {/* Clear All */}
            <button
              type="button"
              onClick={handleClearMask}
              disabled={!hasDrawnContent}
              className="p-1.5 text-stone-400 hover:text-red-400 disabled:opacity-40 rounded-lg hover:bg-stone-800 transition-colors border border-stone-800"
              title="Clear all painted mask"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close damage mask"
              className="p-2.5 text-stone-400 hover:text-stone-100 rounded-lg hover:bg-stone-800 transition-colors min-h-11 min-w-11 inline-flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Canvas Workspace */}
        <div
          ref={containerRef}
          className="flex-1 bg-stone-950 relative flex items-center justify-center p-4 sm:p-8 overflow-hidden"
        >
          {/* Zoomable Drawing Container */}
          <div
            className="relative inline-block max-w-full max-h-full transition-transform duration-75"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
          >
            {/* Background Photographic Image Canvas */}
            <canvas
              ref={imageCanvasRef}
              className="max-h-[64vh] max-w-full object-contain block rounded shadow-2xl border border-stone-800 pointer-events-none"
            />

            {/* Transparent Damage Overlay Canvas */}
            <canvas
              ref={maskCanvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
            />
          </div>

          {/* Floating Instructions */}
          <div className="absolute top-4 left-4 z-10 hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-stone-900/90 border border-stone-800 rounded-xl text-xs text-stone-300 backdrop-blur-sm shadow-md">
            <Info className="w-4 h-4 text-red-400 shrink-0" />
            <span>
              Paint red strokes over deep creases, torn edges, and tape stains.
            </span>
          </div>

          {/* Floating Zoom Controls */}
          <div className="absolute bottom-4 right-4 z-10 inline-flex items-center bg-stone-900/90 border border-stone-800 rounded-lg p-0.5 text-stone-300 text-xs backdrop-blur-sm">
            <button
              type="button"
              onClick={() => setZoom((prev) => Math.max(1, +(prev - 0.25).toFixed(2)))}
              disabled={zoom <= 1}
              className="p-1 hover:text-stone-100 disabled:opacity-40"
              title="Zoom out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="px-1.5 font-mono text-[11px] text-teal-300">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom((prev) => Math.min(3, +(prev + 0.25).toFixed(2)))}
              disabled={zoom >= 3}
              className="p-1 hover:text-stone-100 disabled:opacity-40"
              title="Zoom in"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            {zoom > 1 && (
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="p-1 hover:text-teal-400 transition-colors"
                title="Reset zoom"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3.5 bg-stone-950 border-t border-stone-800 flex items-center justify-between shrink-0">
          <div className="text-xs text-stone-400">
            {hasDrawnContent ? (
              <span className="text-emerald-400 font-medium">
                ✓ Mask painted: Infilling will be constrained strictly to highlighted areas.
              </span>
            ) : (
              <span>No areas painted yet (whole image will be inspected automatically).</span>
            )}
          </div>

          <div className="flex items-center space-x-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-medium text-stone-300 hover:text-stone-100 hover:bg-stone-800 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleApply}
              className="inline-flex items-center space-x-2 px-5 py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl text-xs transition-colors shadow-lg"
            >
              <Check className="w-4 h-4" />
              <span>Apply Damage Mask</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
